import { F0Dialog } from '@factorialco/f0-react'
import {
  DeleteProcurementTypeDocument,
  NewRequestPageDocument,
  ProcurementSettingsPageDocument,
  ProcurementTypeIdentityFragment,
  ProcurementTypesDataCollectionDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  requestType: ProcurementTypeIdentityFragment | null
  onClose: () => void
}

const DeleteRequestTypeModal = ({ requestType, onClose }: Props) => {
  const { company } = useCurrentSession()

  const [deleteProcurementType, { loading }] = useMutation(DeleteProcurementTypeDocument, {
    extractorFn: (data) => data.procurementMutations.deleteProcurementType,
  })

  if (!requestType) return null

  const onArchiveType = async () => {
    const result = await deleteProcurementType({
      variables: {
        id: requestType.id,
        companyId: company.id,
      },
      refetchQueries: [
        ProcurementSettingsPageDocument,
        NewRequestPageDocument,
        ProcurementTypesDataCollectionDocument,
      ],
    })

    const handled = handleResult(result, (errors) => {
      const errorMessage = Object.values(errors).filter(Boolean).join(', ')
      app.setFlash({
        type: 'wrong',
        message: errorMessage || i18n.t('errors.generic_error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('procurement.purchase_requests.settings.types.modals.archive.success'),
      })

      onClose()
    }

    return handled
  }

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t('procurement.purchase_requests.settings.types.modals.archive.title')}
      description={i18n.t(
        'procurement.purchase_requests.settings.types.modals.archive.description'
      )}
      primaryAction={{
        label: i18n.t(
          'procurement.purchase_requests.settings.types.modals.archive.buttons.archive'
        ),
        onClick: onArchiveType,
        loading,
      }}
      secondaryAction={{
        label: i18n.t('procurement.purchase_requests.settings.types.modals.archive.buttons.cancel'),
        onClick: onClose,
      }}
    />
  )
}

export default DeleteRequestTypeModal
